import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useState } from 'react';
import { Platform } from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeRegular,
    FullScreenSpinner,
    isWeb,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../common/misc';
import { captureException } from '../../logger/Logger';
import { getValidSessionBookingDoneRoute } from '../helpers';
import InlineCalendlyWidget from './InlineCalendlyWidget';

const CALENDLY_URL_PREFIX = 'https://calendly.com/';
const HEADER_SIDE_WIDTH = 40;
const HEADER_SIDE_HEIGHT = 40;

const CalendlySession = ({
    headerText,
    calendlyUrl = '',
    doneRoute = '',
}: {
    headerText: string;
    calendlyUrl?: string;
    doneRoute?: string;
}) => {
    const queryClient = useQueryClient();
    const [isWaiting, setIsWaiting] = useState(false);

    const validDoneRoute = getValidSessionBookingDoneRoute(doneRoute);

    if (isEmpty(calendlyUrl) || !calendlyUrl.startsWith(CALENDLY_URL_PREFIX)) {
        captureException(
            new Error(`Invalid Calendly URL supplied: ${calendlyUrl}`),
        );
        return <Redirect href={validDoneRoute} />;
    }

    const onPressBack = () => {
        if (isWaiting) return;
        // Even if the user has not actually rescheduled or cancelled the session
        // we still handle it as if they have because Calendly does not provide
        // a way to know if the user has made changes or is just navigating back
        setIsWaiting(true);
        setTimeout(async () => {
            await queryClient.invalidateQueries({
                queryKey: ['searchGuidanceSessions'],
            });
            // Handle routing differently for iOS because the screen is a modal
            if (router.canGoBack() && Platform.OS === 'ios') {
                router.back();
            } else {
                router.navigate(validDoneRoute);
            }
            setIsWaiting(false);
        }, 2000); // Wait for webhook to settle
    };

    if (isWaiting) {
        return <FullScreenSpinner />;
    }

    return (
        <View style={{ flex: 1 }}>
            <YStack
                marginHorizontal={20}
                marginTop={16}
                flex={1}
                $platform-web={{
                    marginHorizontal: 0,
                    overflow: 'hidden',
                }}
            >
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    paddingHorizontal={isWeb ? 12 : 0}
                >
                    <YStack
                        width={HEADER_SIDE_WIDTH}
                        height={HEADER_SIDE_HEIGHT}
                        alignItems="flex-start"
                        justifyContent="center"
                    >
                        <BackButton
                            iconType={BackButtonIconType.X}
                            customBackFn={onPressBack}
                        />
                    </YStack>
                    <BodyLargeRegular textAlign="center" width={250}>
                        {headerText}
                    </BodyLargeRegular>
                    <XStack
                        width={HEADER_SIDE_WIDTH}
                        height={HEADER_SIDE_HEIGHT}
                    ></XStack>
                </XStack>
                <InlineCalendlyWidget calendlyUrl={calendlyUrl} />
            </YStack>
        </View>
    );
};

export default CalendlySession;
